import { getAuthToken } from "./authApi";

export const PRODUCTS_API_URL = "https://uktomars.co.uk/api/products";
export const CATEGORIES_API_URL = "https://uktomars.co.uk/api/categories";
export const BRANDS_API_URL = "https://uktomars.co.uk/api/brands";
export const EVENTS_API_URL = "https://uktomars.co.uk/api/events";
export const QUOTE_API_URL = "https://uktomars.co.uk/api/quotes";

function formatQuoteErrorMessage(message, status) {
  const text = String(message || "").trim();
  if (!text) {
    return status === 401
      ? "Your session has expired. Please sign in again."
      : "Unable to submit quote request. Please try again.";
  }

  const lower = text.toLowerCase();
  if (
    lower.includes("smtp") ||
    lower.includes("authenticator") ||
    lower.includes("535 5.7.8") ||
    lower.includes("mailer")
  ) {
    return (
      "Your quote could not be completed because the server email account " +
      "(sales@uktomars.co.uk) is not configured correctly. " +
      "Please contact sales@uktomars.co.uk with your basket details, " +
      "or ask your site administrator to update the API mail settings."
    );
  }

  return text;
}

export async function submitQuote({ note = "", lines }) {
  const token = getAuthToken();
  if (!token) {
    const error = new Error(
      "Please sign in to your trade account to request a quote.",
    );
    error.code = "AUTH_REQUIRED";
    throw error;
  }

  const response = await fetch(QUOTE_API_URL, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Accept: "application/json",
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({ note, lines }),
  });

  const payload = await response.json().catch(() => ({}));

  if (!response.ok) {
    const rawMessage = payload?.message || payload?.error || "";
    const message = formatQuoteErrorMessage(rawMessage, response.status);
    const error = new Error(message);
    error.status = response.status;
    error.rawMessage = rawMessage;
    if (response.status === 401) {
      error.code = "AUTH_REQUIRED";
    }
    throw error;
  }

  return payload;
}

export async function fetchQuotes({ page = 1 } = {}) {
  const token = getAuthToken();
  if (!token) {
    const error = new Error("Please sign in to view your quote history.");
    error.code = "AUTH_REQUIRED";
    throw error;
  }

  const url =
    page > 1 ? `${QUOTE_API_URL}?page=${encodeURIComponent(page)}` : QUOTE_API_URL;

  const response = await fetch(url, {
    headers: {
      Accept: "application/json",
      Authorization: `Bearer ${token}`,
    },
  });

  const payload = await response.json().catch(() => ({}));

  if (!response.ok) {
    const message =
      payload?.message ||
      payload?.error ||
      (response.status === 401
        ? "Your session has expired. Please sign in again."
        : "Unable to load quote history. Please try again.");
    const error = new Error(message);
    error.status = response.status;
    if (response.status === 401) {
      error.code = "AUTH_REQUIRED";
    }
    throw error;
  }

  return payload;
}

export async function fetchAllPaginated(url) {
  const all = [];
  let nextUrl = url;

  while (nextUrl) {
    const response = await fetch(nextUrl);
    if (!response.ok) throw new Error(`Failed to fetch ${nextUrl}`);

    const payload = await response.json();
    const data = Array.isArray(payload?.data) ? payload.data : [];
    all.push(...data);
    nextUrl = payload?.links?.next ?? null;
  }

  return all;
}

export const getProductsByBrandUrl = (brandId) => {
  const id = String(brandId ?? "").trim();
  if (!id) return PRODUCTS_API_URL;
  return `${PRODUCTS_API_URL}?brand_id=${encodeURIComponent(id)}`;
};

export const fetchProductsByBrand = (brandId) =>
  fetchAllPaginated(getProductsByBrandUrl(brandId));

export const findBrandById = async (brandId) => {
  const id = String(brandId ?? "").trim();
  if (!id) return null;

  const brands = await fetchAllPaginated(BRANDS_API_URL);
  return brands.find((brand) => String(brand?.id) === id) ?? null;
};

export const getBrandProductsPath = (brand) => {
  if (brand?.id == null) return "/Brands";
  const id = encodeURIComponent(String(brand.id));
  return `/Brands/${id}/products?brand_id=${id}`;
};

export const getProductsListUrl = ({
  category = "",
  subcategory = "",
} = {}) => {
  const params = new URLSearchParams();
  if (subcategory) {
    params.set("category", subcategory);
  } else if (category) {
    params.set("category", category);
  }

  const query = params.toString();
  return query ? `${PRODUCTS_API_URL}?${query}` : PRODUCTS_API_URL;
};

export const getCategoryProductsPath = (categorySlug) =>
  `/products?category=${encodeURIComponent(categorySlug)}`;
